/**
 * Сколько звонили по лиду «Новый»/«В работе» или сделке «В работе» — плашка над полем ответа «Чатов».
 *
 * Попытка — отдельный час со звонком, как считает Резерв: перенабор в тот же час — та же попытка,
 * входящие от клиента тоже в счёте. Лид Резерв сам переводит в «Не дозвонились», когда попыток
 * больше 10 и лиду от 11 дней; клик по плашке — как сработает автоотмена по этому лиду и звонки по дням.
 * Спека `docs/specs/2026-10-26-call-attempts-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Phone } from "lucide-react";
import { useState } from "react";

import { type CallAttempts as Attempts, type ObjectKind, loadCallAttempts } from "@/lib/calls";
import { plural } from "@/lib/plural";

const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const daysTo = (iso: string) => Math.round((Date.parse(iso) - Date.parse(new Date().toISOString().slice(0, 10))) / 86_400_000);

/** Подсказка в строке плашки: когда сработает автоотмена. */
function hint(a: Attempts): string {
  const c = a.cancel;
  if (!c) return "у сделок автоотмены нет";
  if (c.due) return "автоотмена: Резерв отменит лид ближайшим проходом";
  const wait = daysTo(c.from_date);
  if (c.attempts_left > 0 && wait > 0)
    return `автоотмена: через ${c.attempts_left} ${plural(c.attempts_left, "попытку", "попытки", "попыток")}, не раньше ${ddmm(c.from_date)}`;
  if (c.attempts_left > 0) return `автоотмена: через ${c.attempts_left} ${plural(c.attempts_left, "попытку", "попытки", "попыток")}`;
  return `автоотмена: с ${ddmm(c.from_date)} — попытки уже набраны`;
}

function tone(a: Attempts): string {
  const c = a.cancel;
  if (c?.due) return "border-[#e53935]/40 text-[#e53935]";
  if (c && c.attempts_left <= 3) return "border-[#f5a623]/50 text-[#b26a00]";
  return "border-[var(--tg-sec)]/25 text-[var(--tg-sec)]";
}

function Rule({ a }: { a: Attempts }) {
  const c = a.cancel;
  if (!c)
    return (
      <p>
        Сделку Резерв по числу звонков не отменяет — счёт для ориентира. Считаем звонки по контактам сделки и её
        лиду с {ddmm(a.since)} (день создания лида; без лида — сделки).
      </p>
    );
  const wait = daysTo(c.from_date);
  const ok = "text-[#2e9e4f]";
  return (
    <div className="space-y-1">
      <p>
        Робот Резерва сам переводит лид «Новый» или «В работе» в «Не дозвонились» (пометка «много звонков, не
        дозвон»), когда выполнены <b>оба</b> условия:
      </p>
      <ol className="list-decimal space-y-0.5 pl-5">
        <li>
          Попыток больше 10 — сейчас <b>{a.attempts}</b>
          {c.attempts_left > 0 ? `, осталось ${c.attempts_left}` : <span className={ok}> — выполнено</span>}.
        </li>
        <li>
          Лиду не меньше 10 полных дней — с {ddmm(c.from_date)} (создан {ddmm(a.created_at)} в {a.created_at.slice(11, 16)})
          {wait > 0 ? `, ещё ${wait} ${plural(wait, "день", "дня", "дней")}` : <span className={ok}> — выполнено</span>}.
        </li>
      </ol>
      <p className="font-medium text-[var(--tg-text)]">
        {c.due
          ? "Оба условия есть: лид отменится ближайшим проходом робота."
          : c.attempts_left > 0 && wait > 0
            ? `Сработает после ${c.attempts}-й попытки, но не раньше ${ddmm(c.from_date)}.`
            : c.attempts_left > 0
              ? `Сработает сразу после ${c.attempts}-й попытки.`
              : `Сработает ${ddmm(c.from_date)} — попыток уже хватает.`}
      </p>
      <p>
        Попытка — отдельный час, в котором был звонок: перенабор в тот же час не считается, входящий от клиента —
        считается, разговор счёт не сбрасывает. Лид, ставший сделкой или отказом, робот не трогает.
      </p>
    </div>
  );
}

export function CallAttempts({ kind, id }: { kind: ObjectKind; id: number }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["call-attempts", kind, id],
    queryFn: () => loadCallAttempts(kind, id),
    // Звонок идёт мимо «Чатов» (MicroSIP, Bitrix) — подтягиваем, не перезагружая чат.
    refetchInterval: 60_000,
  });
  const a = q.data;
  if (!a) return null;
  return (
    <div
      className={`mb-1.5 rounded-[14px] border bg-[var(--tg-surface)] px-3 py-1 text-[13px] shadow-[var(--tg-shadow)] ${tone(a)}`}
      data-annot="попытки дозвона"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 text-left"
        title="Как сработает автоотмена и звонки по дням"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Phone size={13} />
        <span className="font-medium text-[var(--tg-text)]">{kind === "lead" ? "Лид" : "Сделка"}</span>
        <span>
          звонили {a.days} {plural(a.days, "день", "дня", "дней")}
        </span>
        <span>
          · попыток <b className="text-[var(--tg-text)]">{a.attempts}</b>
          {a.cancel && (a.cancel.attempts_left > 0 ? ` из ${a.cancel.attempts}` : `, порог ${a.cancel.attempts} набран`)}
        </span>
        <span>· разговоров {a.talks}</span>
        {kind === "lead" && (
          <span>
            · лиду {a.age_days} {plural(a.age_days, "день", "дня", "дней")}
          </span>
        )}
        <span className="basis-full pl-[34px] sm:basis-auto sm:pl-0">· {hint(a)}</span>
      </button>
      {open && (
        <div className="mt-1.5 max-h-[34vh] space-y-2 overflow-y-auto pb-1 text-[var(--tg-sec)]">
          <Rule a={a} />
          {a.by_day.length > 0 ? (
            <table className="w-full max-w-[420px] text-[12.5px]">
              <thead>
                <tr className="text-left">
                  <th className="font-normal">День</th>
                  <th className="text-right font-normal">попыток</th>
                  <th className="text-right font-normal">звонков</th>
                  <th className="text-right font-normal">разговоров</th>
                </tr>
              </thead>
              <tbody className="text-[var(--tg-text)]">
                {a.by_day.map((d) => (
                  <tr key={d.date}>
                    <td>{ddmm(d.date)}</td>
                    <td className="text-right">{d.attempts}</td>
                    <td className="text-right">{d.calls}</td>
                    <td className="text-right">{d.talks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>Звонков ещё не было.</p>
          )}
        </div>
      )}
    </div>
  );
}
